import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const iui: ContentBlock<"procedure"> = {
  id: makeId("proc-iui"),
  kind: "procedure",
  tags: ["clinic-visit", "both-partners", "iui", "treatment", "during-cycle"],
  title: "IUI Procedure",
  subtitle:
    "Intrauterine insemination: washed sperm placed directly into the uterus",
  body: {
    procedureName: "Intrauterine Insemination (IUI)",
    category: "transfer",
    description:
      "An IUI is a fertility treatment in which concentrated, washed sperm are placed directly into the uterus through a thin catheter, timed to coincide with ovulation. It shortens the distance sperm must travel and increases the number that reach the egg.",
    howItWorks:
      "On the morning of the IUI, a semen sample is collected (or a frozen sample is thawed) and processed in the lab through a technique called sperm washing, which concentrates the healthiest, most motile sperm into a small volume. The procedure itself is performed in an exam room and feels similar to a Pap smear. A speculum is placed, and a thin, flexible catheter is threaded through the cervix into the uterine cavity. The washed sperm are slowly injected, and the catheter is removed. The entire insemination takes about 1-2 minutes. You typically rest for 5-10 minutes afterward, though studies show this is more for comfort than medical necessity. Most patients return to normal activities the same day.",
    duration: "15-20 minutes (including rest time)",
    costRange: { low: 300, high: 1000 },
    currency: "USD",
    anesthesia: "none",
    recoveryTime: "Resume normal activities same day",
    risks: [
      "Mild cramping during or after the procedure",
      "Light spotting afterward",
      "Small risk of infection",
      "Multiple pregnancy if used with ovulation-stimulating medications",
    ],
    prepInstructions: [
      "Confirm the timing of your trigger shot with your clinic, the IUI is usually 24-36 hours after",
      "If the cycle is monitored, the lead follicle is often around 18-24 mm at trigger, but your clinic will use the full ultrasound and hormone picture",
      "If using a fresh sample, your partner should abstain for 2-5 days before collection day",
      "Eat normally and stay hydrated",
      "Take ibuprofen only if your doctor approves, some clinics advise against it around implantation",
      "Arrive on time, the sample processing takes about 1 hour before the insemination",
    ],
    whatToExpect: [
      "Your partner or donor sample will be processed in the lab (about 30-60 minutes)",
      "A speculum is placed and the cervix is cleaned",
      "A thin catheter is gently inserted through the cervix, you may feel a brief cramp",
      "The washed sperm are injected in a few seconds",
      "You rest for 5-10 minutes, then you are free to go",
      "Light spotting afterward is normal",
      "Bring insurance card or payment method",
      "Bring your partner (if providing a fresh sample at the clinic)",
      "Bring a pad or panty liner",
      "Bring comfortable, relaxing items if you want them for the brief rest period (e.g., headphones)",
    ],
    results: [
      {
        label: "Sample processing",
        detail:
          "The lab may share post-wash sperm count or motility, but not every clinic gives this number the same day. A post-wash total motile sperm count above about 10 million is often considered a stronger IUI scenario.",
      },
      {
        label: "Follicle timing",
        detail:
          "For medicated or monitored IUI, a lead follicle around 18-24 mm is often used to time trigger or insemination, depending on the protocol.",
      },
      {
        label: "Pregnancy test timing",
        detail:
          "The outcome is usually checked with a pregnancy test about 12-14 days after IUI, or on the date your clinic gives you.",
      },
      {
        label: "Trigger shot caveat",
        detail:
          "If you used an hCG trigger shot, early home pregnancy tests can be falsely positive because of the medication.",
      },
    ],
    forWhom: "both",
    frBody: {
      procedureName: "Insémination intra-utérine (IIU)",
      description:
        "L'IIU est un traitement de fertilité au cours duquel des spermatozoïdes préparés et concentrés sont déposés directement dans l'utérus à l'aide d'un fin cathéter, en coïncidence avec l'ovulation. Cela raccourcit la distance que doivent parcourir les spermatozoïdes et augmente le nombre de ceux qui atteignent l'ovocyte.",
      howItWorks:
        "Le matin de l'IIU, un échantillon de sperme est recueilli (ou un échantillon congelé est décongelé) et préparé au laboratoire par une technique de lavage qui concentre les spermatozoïdes les plus sains et mobiles dans un faible volume. Le geste lui-même est réalisé en salle d'examen et ressemble à un frottis. Un spéculum est placé, et un cathéter fin et souple est introduit à travers le col jusque dans la cavité utérine. Les spermatozoïdes préparés sont injectés lentement, puis le cathéter est retiré. L'insémination elle-même prend environ 1 à 2 minutes. Vous restez ensuite allongée 5 à 10 minutes, davantage pour le confort que par nécessité médicale. La plupart des patientes reprennent leurs activités normales le jour même.",
      duration: "15 à 20 minutes (temps de repos compris)",
      recoveryTime: "Reprise des activités normales le jour même",
      risks: [
        "Crampes légères pendant ou après le geste",
        "Léger spotting après",
        "Faible risque d'infection",
        "Grossesse multiple en cas d'association à une stimulation de l'ovulation",
      ],
      prepInstructions: [
        "Confirmez l'heure du déclencheur avec votre centre ; l'IIU est généralement réalisée 24 à 36 heures après",
        "Si le cycle est suivi, le follicule dominant mesure souvent environ 18 à 24 mm au moment du déclenchement, mais le centre utilise l'ensemble de l'échographie et des hormones",
        "Pour un échantillon frais, votre partenaire doit observer 2 à 5 jours d'abstinence avant le recueil",
        "Mangez normalement et hydratez-vous bien",
        "Ne prenez de l'ibuprofène qu'avec accord du médecin ; certains centres le déconseillent autour de l'implantation",
        "Arrivez à l'heure : la préparation du sperme prend environ 1 heure avant l'insémination",
      ],
      whatToExpect: [
        "L'échantillon (partenaire ou donneur) est préparé au laboratoire (30 à 60 minutes)",
        "Un spéculum est placé et le col est désinfecté",
        "Un fin cathéter est introduit délicatement à travers le col ; vous pouvez ressentir une brève crampe",
        "Les spermatozoïdes préparés sont injectés en quelques secondes",
        "Vous restez allongée 5 à 10 minutes, puis vous pouvez repartir",
        "Un léger spotting ensuite est normal",
        "Apportez votre carte Vitale/mutuelle ou un moyen de paiement",
        "Venez avec votre partenaire s'il fournit un échantillon frais à la clinique",
        "Prévoyez une serviette ou un protège-slip",
        "Apportez des éléments de confort si vous le souhaitez pour le bref temps de repos (écouteurs, par exemple)",
      ],
      results: [
        {
          label: "Préparation du sperme",
          detail:
            "Le laboratoire peut communiquer la numération ou la mobilité après préparation, mais tous les centres ne donnent pas ce chiffre le jour même. Un total de spermatozoïdes mobiles après préparation supérieur à environ 10 millions est souvent considéré comme une situation plus favorable pour l'IIU.",
        },
        {
          label: "Timing folliculaire",
          detail:
            "Pour une IIU stimulée ou surveillée, un follicule dominant autour de 18 à 24 mm sert souvent à programmer le déclenchement ou l'insémination, selon le protocole.",
        },
        {
          label: "Test de grossesse",
          detail:
            "Le résultat est généralement vérifié par un test de grossesse environ 12 à 14 jours après l'IIU, ou à la date donnée par votre centre.",
        },
        {
          label: "Attention au déclencheur",
          detail:
            "Si vous avez utilisé un déclencheur hCG, les tests urinaires trop précoces peuvent être faussement positifs à cause du médicament.",
        },
      ],
    },
  },
  sources: [
    {
      title:
        "Use of exogenous gonadotropins for ovulation induction in anovulatory women: a committee opinion",
      authors: "Practice Committee of the ASRM in collaboration with SREI",
      journal: "Fertility and Sterility",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/use-of-exogenous-gonadotropins-for-ovulation-induction-in-anovulatory-women-a-committee-opinion-2020/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(iui);
